import { ScreenContext } from '@/contexts/ScreenContext';
import { getGiftRecordReportState } from '@/modules/report/selector';
import { RootState } from '@/store/store';
import { AmountText } from '@/utils/formatter';
import { ArrowForwardIosOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, styled } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { TFunction } from 'i18next';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GameType as GameTypeEnum } from '../../../../models/games/enums/GameType';
import { Gift, GiftRecordTotalResult } from '../../../report/slice/giftRecord';
import { ReportDivider } from '../ReportDivider';
import { GiftDetail, RoundText } from './GiftDetails';
import './index.scss';

type CloseHandler = () => void;
type GiftRecordDetailsReportProps = {
    onClose: CloseHandler;
    playButtonAudio: CloseHandler;
};
const DetailDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        margin: '0',
        borderRadius: '0.125rem',
        background: '#36302C',
        boxShadow: '0px 0px 24.083px 0px rgba(0, 0, 0, 0.35)',
        backdropFilter: 'blur(32.11073303222656px)',
        width: '83.33333333333333%',
        height: 'auto',
        maxWidth: '100%',
    },
}));

export const GiftRecordDetailsReport = (
    props: GiftRecordDetailsReportProps
) => {
    const {
        // onClose,
        playButtonAudio,
    } = props;
    const [collapseIndex, setCollapseIndex] = useState(-1);
    const [curBets, setCurBets] = useState<Array<Gift>>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [record, setRecord] = useState<GiftRecordTotalResult>();
    const { t } = useTranslation();
    const { records } = useSelector((state: RootState) =>
        getGiftRecordReportState(state)
    );
    const { isLandscape } = useContext(ScreenContext);

    useEffect(() => {
        if (records) {
            setRecord(records);
        }
    }, [records]);

    const [scrollPaddingBottom, setScrollPaddingBottom] = useState(
        isLandscape ? 'max(calc(var(--vh) * 1 * 3), 1rem)' : '1rem'
    );
    useEffect(() => {
        if (record) {
            setCurBets(record.gifts);
            setScrollPaddingBottom(
                isLandscape ? 'max(calc(var(--vh) * 1 * 3), 1rem)' : '1rem'
            );
        }
    }, [record, isLandscape]);

    const [displayDetailList, setDisplayDetailList] = useState<JSX.Element>(
        <></>
    );
    useEffect(() => {
        if (curBets) {
            setDisplayDetailList(
                <Box
                    paddingBottom={scrollPaddingBottom}
                    flexGrow={1}
                    className={'hide-scroll'}
                >
                    <DetailList />
                </Box>
            );
        }
    }, [curBets, dialogOpen]);

    const DetailList = () => {
        return curBets && curBets.length > 0 ? (
            <List sx={{ paddingY: 0 }}>
                {curBets &&
                    curBets.map((bet, i) => {
                        const gameName = GetGameName(
                            bet.GameType,
                            bet.HostID,
                            t
                        );
                        return (
                            <div key={`list-detail-${bet.ID}-${i}`}>
                                <ListItem sx={{ padding: 0 }}>
                                    <ListItemButton
                                        onClick={() => {
                                            setCollapseIndex(i);
                                            setDialogOpen(true);
                                        }}
                                        selected={collapseIndex === i}
                                        sx={{ paddingY: 0 }}
                                    >
                                        <ListItemText>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                sx={{ fontSize: '0.85rem' }}
                                            >
                                                <BetRecordDetailCell
                                                    first={`${bet.Date} / ${bet.Time}`}
                                                    second={
                                                        <RoundText
                                                            bet={bet}
                                                            gameName={gameName}
                                                        />
                                                    }
                                                    third={
                                                        <AmountText
                                                            amount={
                                                                bet.GiftAmount /
                                                                100
                                                            }
                                                            with_color={false}
                                                            textAlign={'end'}
                                                        />
                                                    }
                                                    isTitle={false}
                                                />
                                            </Stack>
                                        </ListItemText>
                                    </ListItemButton>
                                </ListItem>
                                <ReportDivider isCell={true} />
                                {dialogOpen && (
                                    <DetailDialog
                                        open={collapseIndex === i}
                                        onClose={() => {
                                            setDialogOpen(false);
                                        }}
                                        className="bet-record-report"
                                        disableEscapeKeyDown
                                    >
                                        <IconButton
                                            disableRipple
                                            onClick={() => {
                                                setDialogOpen(false);
                                                playButtonAudio();
                                            }}
                                            sx={{
                                                padding: '0.45rem',
                                                width: 'fit-content',
                                                alignSelf: 'flex-end',
                                            }}
                                        >
                                            <CloseIcon fontSize="medium" />
                                        </IconButton>
                                        <GiftDetail
                                            bet={bet}
                                            gameName={gameName}
                                        />
                                    </DetailDialog>
                                )}
                            </div>
                        );
                    })}
            </List>
        ) : (
            <Grid item container className="gift-report-notice-message">
                {t('no_gift_record')}
            </Grid>
        );
    };

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    color: '#ae9268',
                    paddingX: '1rem',
                }}
                height={'max(calc(var(--vh) * 2 *3), 2rem)'}
            >
                <BetRecordDetailCell
                    first={`${t('betlog.date')} / ${t('betlog.time')}`}
                    second={`${t('betlog.game')} / ${t('betlog.round')}`}
                    third={t('gift_amount')}
                    isTitle={true}
                />
            </Stack>
            <Grid item>
                <ReportDivider />
            </Grid>
            {displayDetailList}
        </>
    );
};

export const BetRecordDetailCell = ({
    first,
    second,
    third,
    isTitle,
}: {
    first: ReactNode;
    second: ReactNode;
    third: ReactNode;
    isTitle: boolean;
}) => {
    return (
        <>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="bet-record-report"
            >
                <Grid
                    item
                    container
                    alignItems="center"
                    justifyContent="flex-start"
                    width={'27%'}
                    height={'3rem'}
                >
                    {first}
                </Grid>
                <Grid
                    item
                    container
                    alignItems="center"
                    justifyContent="center"
                    width={'40%'}
                    minHeight={'3rem'}
                    textAlign={'center'}
                >
                    {second}
                </Grid>
                <Grid
                    item
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    width={'30%'}
                    height={'3rem'}
                >
                    {isTitle ? (
                        third
                    ) : (
                        <Grid
                            display={'flex'}
                            gap={'10px'}
                            alignItems={'center'}
                            width={'100%'}
                        >
                            {third}
                            <Grid
                                item
                                display="flex"
                                alignItems="baseline"
                                // margin="0 0 0 0.5rem"
                            >
                                <ArrowForwardIosOutlined
                                    sx={{
                                        color: '#BF9A5D',
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

const GetGameName = (
    gameType: number,
    hostId: number,
    t: TFunction
): string => {
    switch (gameType) {
        //TODO: other game
        case GameTypeEnum.Baccarat:
        case GameTypeEnum.SqueezeBaccarat:
            return (
                t('baccarat.gametype') + ' - ' + t(`hostCodeNames.${hostId}`)
            );
        case GameTypeEnum.SeDie:
        case GameTypeEnum.Dragon:
        case GameTypeEnum.PokDeng:
        case GameTypeEnum.SicBo:
        case GameTypeEnum.ThaiHiLo:
        case GameTypeEnum.AndarBahar:
        case GameTypeEnum.TeenPatti2020:
        case GameTypeEnum.Roulette:
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.Blackjack:
        case GameTypeEnum.Blackjack2:
        case GameTypeEnum.FishPrawnCrab:
        case GameTypeEnum.CarnivalTreasure:
            return t(`hostList.${hostId}`);
        default:
            return '';
    }
};
